/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useCallback, useEffect, useRef, useState } from "react";

import * as React from "react";
import PlayPauseButton from "components/PlayPauseButton";
import { CompletedEnhanceSpeechTrack } from "./Utilities";
import styled from "styled-components";
import { devices, fontFamilies } from "components/shared/Styles";
import Slider from "rc-slider";
import { formatSeconds } from "TimeHelper";
import useEnhancedStreamingAudioPlayer from "components/EnhancedSpeech/useEnhancedStreamingAudioPlayer";

const PlayerContainer = styled.div.attrs({ "data-testid": "player-container" })`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${devices.small} {
    margin-bottom: 16px;
  }
`;

const SeekContainer = styled.div.attrs({ "data-testid": "seek-container" })`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

const Timecode = styled.div.attrs({
  "aria-label": I18n.t("enhance_speech.preview_player.current_time"),
})`
  font: normal normal normal 18px/22px ${fontFamilies};
  flex-shrink: 0;
  width: 30px;
  padding-bottom: 3px;

  ${devices.small} {
    padding-right: 5px;
  }
`;

const Duration = styled.div.attrs({
  "aria-label": I18n.t("enhance_speech.preview_player.duration"),
})`
  font: normal normal normal 18px/22px ${fontFamilies};
  padding-bottom: 3px;
`;

const Scrubber = styled(Slider)<{ bufferedRegionPercent: number }>`
  .rc-slider-handle {
    border-color: var(--spectrum-gray-800);
    background-color: var(--spectrum-white);
    border-width: 3px;
    opacity: 1;

    &:hover,
    &:focus {
      border-color: var(--spectrum-gray-800);
    }
  }

  ${devices.small} {
    .rc-slider-handle {
      width: 24px;
      height: 24px;
      margin-top: -10px;
    }
  }

  .rc-slider-track {
    background-color: var(--spectrum-gray-800);
    border-radius: 2px;
  }

  .rc-slider-rail {
    background: transparent
      linear-gradient(
        90deg,
        var(--spectrum-gray-400) 0%,
        var(--spectrum-gray-400)
          ${({ bufferedRegionPercent }): number => bufferedRegionPercent - 0.1}%,
        var(--spectrum-gray-200)
          ${({ bufferedRegionPercent }): number => bufferedRegionPercent}%
      )
      0% 0% no-repeat padding-box;
    border-radius: 2px;
  }
`;

const VideoContainer = styled.div.attrs({ "data-testid": "video-container" })`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 Aspect Ratio -> 9 / 16 = 0.5625 */
  overflow: hidden;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    180deg,
    rgba(242.25, 242.25, 242.25, 0.36) 0%,
    #f1f1f1 100%
  );
  margin-bottom: 16px;

  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const ENHANCED_VIDEO_ENABLED = process.env.ENHANCED_VIDEO_ENABLED === "true";

const EnhancedTrackPlayer: React.FC<{
  track: CompletedEnhanceSpeechTrack;
  enhanceStrengthPercent: number;
  backgroundPercent: number;
  enhancementEnabled: boolean;
}> = ({
  track,
  enhanceStrengthPercent,
  backgroundPercent,
  enhancementEnabled,
}) => {
  const videoContainerElementRef = useRef<HTMLDivElement | null>(null);

  const {
    play,
    pause,
    seekTo,
    isPlaying,
    bufferedRegionPercent,
    currentTime,
    readyToPlay,
  } = useEnhancedStreamingAudioPlayer(
    enhanceStrengthPercent,
    backgroundPercent,
    enhancementEnabled,
    track,
    videoContainerElementRef
  );

  const [isSeeking, setIsSeeking] = useState(false);
  const [scrubberPosition, setScrubberPosition] = useState(0);

  const togglePlayButton = useCallback(async () => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, pause, play]);

  const durationInSeconds = track.duration ?? 0;

  // Update scrubber position when current player time changes unless we're in the middle of a seek.
  useEffect(() => {
    if (isSeeking) {
      return;
    }

    setScrubberPosition(currentTime);
  }, [currentTime, isSeeking]);

  // Reset all state when track changes
  useEffect(() => {
    setIsSeeking(false);
    setScrubberPosition(0);
  }, [track]);

  const onBeforeChange = useCallback((value: number): void => {
    setIsSeeking(true);
  }, []);

  const onChange = useCallback((value: number): void => {
    setScrubberPosition(value);
  }, []);

  const onChangeComplete = useCallback(
    (value: number): void => {
      seekTo(value);
      setIsSeeking(false);
    },
    [seekTo]
  );

  return (
    <div>
      {ENHANCED_VIDEO_ENABLED && track.isVideo && (
        <VideoContainer
          onClick={(): void => (isPlaying ? pause() : play())}
          ref={videoContainerElementRef}
        />
      )}
      <PlayerContainer>
        <SeekContainer>
          <PlayPauseButton
            isPlaying={isPlaying}
            trackDescription={track.trackName}
            buttonDiameterPx={48}
            onClick={togglePlayButton}
            isLoaded={readyToPlay}
          />
          <Timecode>{formatSeconds(scrubberPosition)}</Timecode>
          <Scrubber
            onBeforeChange={onBeforeChange}
            onChange={onChange}
            onChangeComplete={onChangeComplete}
            max={durationInSeconds}
            value={scrubberPosition}
            step={0.1}
            bufferedRegionPercent={bufferedRegionPercent}
          />
          <Duration>{formatSeconds(durationInSeconds)}</Duration>
        </SeekContainer>
      </PlayerContainer>
    </div>
  );
};

export default EnhancedTrackPlayer;
